import { ulid } from 'ulid'
import { isEmpty } from '@/utils/tools'
import React, { createContext, useState, useMemo, useCallback, useEffect, useContext, useRef } from 'react'
// Wizard Context
const WizardContext = createContext(null)

const defaultBaseState = {
    id: ulid(),
    currentStep: 0,
    currentStepName: null,
    stepOrder: [],
    viewModel: {},
    contentCache: {},
    showGuidePopups: true,
}

// Generic Wizard Provider
const WizardProvider = ({
    storageKey,
    initialState = {},
    children,
    onStateChange,
    idExtractor,
    existingId,
    initialContentCache,
    editCreativeId,
    startAtReviewSave,
    startAtStep,
    cloneCreativeId,
    campaignId: campaignIdProp,
}) => {
    const extractedId = idExtractor ? idExtractor() : null
    const thisStorageKey = extractedId ? `${storageKey}-${extractedId}` : storageKey

    const mergedDefaultState = {
        ...defaultBaseState,
        ...initialState,
    }

    const initialTree = useMemo(() => {
        const vm = {}
        let order = []

        function walk(nodes, parent = null) {
            const childArray = React.Children.toArray(nodes)

            childArray.forEach((node) => {
                if (!React.isValidElement(node)) {
                    return
                }

                const { name, label, visible } = node.props

                if (!name) {
                    if (Array.isArray(node.props.children) && node.props.children.length) {
                        walk(node.props.children, parent)
                        return
                    } else {
                        return
                    }
                }

                if (!parent) {
                    vm[name] = {
                        name,
                        label,
                        visible: visible === undefined ? true : visible,
                        complete: 0,
                        children: {},
                    }
                } else {
                    vm[parent].children[name] = {
                        name,
                        label,
                        visible: vm[parent]?.visible,
                        complete: 0,
                        children: {},
                    }
                }

                if (parent === null && name && visible !== false) {
                    order.push(name)
                    order = [...new Set(order)]
                }

                walk(node.props.children, name)
            })
        }

        walk(children, null)
        return { vm, order }
    }, [children])

    const createInitialState = () => {
        let saved = null
        try {
            if (typeof window !== 'undefined' && window.localStorage) {
                if (editCreativeId || startAtReviewSave) {
                    window.localStorage.removeItem(thisStorageKey)
                } else {
                    saved = JSON.parse(window.localStorage.getItem(thisStorageKey))
                }
            }
        } catch (error) {
            // Ignore localStorage errors
        }

        // Initialize showGuidePopups from localStorage (backward compatibility)
        let showGuidePopups = true
        try {
            if (typeof window !== 'undefined' && window.localStorage) {
                const stored = window.localStorage.getItem('showAdCampaignGuide')
                if (stored !== null) {
                    showGuidePopups = stored === 'true'
                }
            }
        } catch (error) {
            // Ignore localStorage errors
        }

        const order = saved?.stepOrder?.length ? saved.stepOrder : initialTree.order

        const applyStartStep = (state) => {
            if (!editCreativeId && !startAtReviewSave) return state
            const targetStep = startAtStep || 'reviewSave'
            const stepIndex = order.indexOf(targetStep)
            if (stepIndex === -1) return state
            return {
                ...state,
                ...(editCreativeId && { editCreativeId }),
                currentStepName: targetStep,
                currentStep: stepIndex,
            }
        }

        if (saved) {
            const restored = {
                ...mergedDefaultState,
                ...saved,
                viewModel: {
                    ...initialTree.vm,
                    ...saved.viewModel,
                },
                stepOrder: order,
                currentStepName: saved.currentStepName || 'selectFormat',
                showGuidePopups: saved.showGuidePopups ?? showGuidePopups,
            }
            return applyStartStep(restored)
        }

        const baseState = {
            ...mergedDefaultState,
            id: existingId || ulid(),
            viewModel: initialTree.vm,
            stepOrder: order,
            currentStepName: initialTree.order[0] || null,
            showGuidePopups,
        }

        if (initialContentCache) {
            baseState.contentCache = { ...baseState.contentCache, ...initialContentCache }
        }

        return applyStartStep(baseState)
    }

    const createInitialStateRef = useRef(createInitialState)
    createInitialStateRef.current = createInitialState

    const [state, setState] = useState(createInitialState())

    useEffect(() => {
        try {
            if (typeof window !== 'undefined' && window.localStorage) {
                window.localStorage.setItem(thisStorageKey, JSON.stringify(state))
            }
        } catch (error) {
            // console.warn('Failed to save wizard state to localStorage:', error)
        }

        if (onStateChange) {
            onStateChange(state)
        }
    }, [state, thisStorageKey, onStateChange])

    const [nextGuards, setNextGuards] = useState([])
    // Box payload so we never pass a bare function to setState (React would treat it as an updater).
    const [customNavBox, setCustomNavBox] = useState(null)
    const customNavigation = customNavBox?.payload ?? null

    const markStepAs = (update = 0, stepIndex) => {
        const currentStepName = state.stepOrder[stepIndex !== undefined ? stepIndex : state.currentStep]
        if (!currentStepName) return

        setState((prev) => {
            const vm = prev.viewModel
            const stepData = vm[currentStepName] || {}
            const children = stepData.children || {}

            const parentEntry = Object.entries(vm).find(([, data]) => data.children?.[currentStepName] != null)
            if (parentEntry) {
                const [parentName, parentData] = parentEntry
                const newChildren = {
                    ...parentData.children,
                    [currentStepName]: {
                        ...parentData.children[currentStepName],
                        complete: update,
                    },
                }

                const vals = Object.values(newChildren)
                    .filter((c) => c.visible !== false)
                    .map((c) => c.complete ?? 0)

                const parentComplete = vals.length > 0 ? vals.reduce((a, b) => a + b, 0) / vals.length : 1

                return {
                    ...prev,
                    viewModel: {
                        ...vm,
                        [parentName]: {
                            ...parentData,
                            children: newChildren,
                            complete: parentComplete,
                        },
                    },
                }
            }

            if (Object.keys(children).length > 0) {
                const visibleChildren = Object.values(children).filter((c) => c.visible !== false)
                const parentComplete =
                    visibleChildren.length > 0
                        ? visibleChildren.reduce((sum, c) => sum + (c.complete ?? 0), 0) / visibleChildren.length
                        : update

                return {
                    ...prev,
                    viewModel: {
                        ...vm,
                        [currentStepName]: {
                            ...stepData,
                            complete: parentComplete,
                        },
                    },
                }
            }

            return {
                ...prev,
                viewModel: {
                    ...vm,
                    [currentStepName]: {
                        ...stepData,
                        complete: update,
                    },
                },
            }
        })
    }

    const setChildrenVisibility = (stepName, visibility = true, extView, extOrder) => {
        const newViewModel = extView || { ...state.viewModel }
        const stepOrder = extOrder || [...state.stepOrder]

        Object.keys(newViewModel[stepName].children).forEach((childName) => {
            newViewModel[stepName].children[childName].visible = visibility
        })

        const parentIndex = stepOrder.findIndex((step) => step === stepName)
        const childSteps = []
        const listChildSteps = []
        Object.keys(newViewModel[stepName].children).forEach((child) => {
            if (newViewModel[stepName].children[child].visible) {
                childSteps.push(child)
            }
            listChildSteps.push(newViewModel[stepName].children[child].name)
        })
        const newStepOrder = [...stepOrder.filter((step) => !listChildSteps.includes(step))]
        newStepOrder.splice(parentIndex + 1, 0, ...childSteps)

        setState((prev) => ({
            ...prev,
            viewModel: { ...newViewModel },
            stepOrder: [...new Set(newStepOrder)],
        }))
    }

    const setVisible = (stepName, visibility = true) => {
        if (!state.viewModel[stepName]) return

        const newViewModel = { ...state.viewModel }
        newViewModel[stepName].visible = visibility

        const newStepOrder = []
        Object.keys(newViewModel).forEach((step) => {
            if (newViewModel[step].visible) {
                newStepOrder.push(step)
            }
        })

        setState((prev) => ({
            ...prev,
            viewModel: { ...newViewModel },
            stepOrder: [...new Set(newStepOrder)],
        }))

        if (visibility === false && !isEmpty(newViewModel[stepName].children)) {
            setChildrenVisibility(stepName, visibility, newViewModel, newStepOrder)
        }
    }

    const goNext = async () => {
        for (const guard of nextGuards) {
            if ((await guard(state.viewModel)) === false) return
        }

        // Always mark step as complete when moving forward
        markStepAs(1)
        const step = Math.min(state.currentStep + 1, state.stepOrder.length - 1)
        const stepName = state.stepOrder[step]

        setState((prev) => ({
            ...prev,
            currentStep: step,
            currentStepName: stepName,
        }))
        purgeNextGuards()
    }

    const goBack = () => {
        markStepAs(0)

        const step = Math.max(state.currentStep - 1, 0)
        const stepName = state.stepOrder[step]

        setState((prev) => ({
            ...prev,
            currentStep: step,
            currentStepName: stepName,
        }))

        markStepAs(0, step)
        purgeNextGuards()
    }

    const goTo = (idx) => {
        const step = Math.max(0, Math.min(idx, state.stepOrder.length - 1))
        const stepName = state.stepOrder[step]

        setState((prev) => ({
            ...prev,
            currentStep: step,
            currentStepName: stepName,
        }))

        purgeNextGuards()
    }

    const goToStep = (stepName) => {
        let stepIndex = state.stepOrder.findIndex((step) => step === stepName)
        if (stepIndex === -1) {
            stepIndex = state.currentStep
        }

        setState((prev) => ({
            ...prev,
            currentStep: stepIndex,
            currentStepName: stepName,
        }))

        purgeNextGuards()
    }

    const registerSteps = (names) => setState((prev) => ({ ...prev, stepOrder: names }))

    const setData = (dataUpdate) =>
        setState((prev) => ({
            ...prev,
            viewModel: { ...prev.viewModel, ...dataUpdate },
        }))

    const currentStepName = state.currentStepName

    const getStepContentData = useCallback(
        (stepName) => {
            const thisStepName = stepName || currentStepName
            if (!state.contentCache[thisStepName]) return {}
            return state.contentCache[thisStepName]
        },
        [state.contentCache, currentStepName],
    )

    const setStepContentData = useCallback(
        (dataUpdate, stepName) => {
            setState((prev) => {
                const thisStepName = stepName || currentStepName
                const newCache = { ...prev.contentCache }
                if (!newCache[thisStepName]) {
                    newCache[thisStepName] = {}
                }

                newCache[thisStepName] = {
                    ...newCache[thisStepName],
                    ...dataUpdate,
                }
                return {
                    ...prev,
                    contentCache: newCache,
                }
            })
        },
        [currentStepName],
    )

    const removeStepContentData = (stepName, dataKey) => {
        setState((prev) => {
            const newCache = { ...prev.contentCache }
            if (newCache[stepName] && dataKey && newCache[stepName][dataKey]) {
                delete newCache[stepName][dataKey]
            } else if (newCache[stepName] && isEmpty(dataKey)) {
                delete newCache[stepName]
            }

            return {
                ...prev,
                contentCache: newCache,
            }
        })
    }

    const clearStepAndChildren = useCallback((stepName) => {
        setState((prev) => {
            const newCache = { ...prev.contentCache }
            const newViewModel = { ...prev.viewModel }

            // Recursive function to clear a step and all its children
            const clearStep = (name, parentName = null) => {
                // Clear the step's data
                if (newCache[name]) {
                    delete newCache[name]
                }

                // Reset completion status to 0
                if (parentName && newViewModel[parentName]?.children?.[name]) {
                    newViewModel[parentName].children[name] = {
                        ...newViewModel[parentName].children[name],
                        complete: 0,
                    }
                } else if (newViewModel[name]) {
                    newViewModel[name] = {
                        ...newViewModel[name],
                        complete: 0,
                    }
                }

                // Clear all children recursively
                const stepData = parentName ? newViewModel[parentName]?.children?.[name] : newViewModel[name]

                if (stepData && stepData.children) {
                    Object.keys(stepData.children).forEach((childName) => {
                        clearStep(childName, name)
                    })
                }
            }

            clearStep(stepName)

            return {
                ...prev,
                contentCache: newCache,
                viewModel: newViewModel,
            }
        })
    }, [])

    const registerNextGuard = useCallback((guardFn) => {
        if (typeof guardFn !== 'function') return guardFn
        setNextGuards((prevGuards) => {
            // Check if this guard is already registered by reference
            const existingGuard = prevGuards.find((g) => g === guardFn)
            if (existingGuard) {
                return prevGuards // No change needed
            }
            const unique = [...prevGuards, guardFn]
            return unique
        })
        return guardFn
    }, [])

    const deregisterNextGuard = useCallback((guardFn) => {
        if (typeof guardFn !== 'function') return
        setNextGuards((prevGuards) => {
            const filtered = prevGuards.filter((g) => g !== guardFn)
            return filtered
        })
    }, [])

    const purgeNextGuards = useCallback(() => {
        setNextGuards([])
    }, [])

    const hasActiveGuards = useCallback(() => {
        return nextGuards.length > 0
    }, [nextGuards])

    const registerCustomNavigation = useCallback((navigationComponent) => {
        setCustomNavBox({ payload: navigationComponent })
    }, [])

    const clearCustomNavigation = useCallback(() => {
        setCustomNavBox(null)
    }, [])

    const clearWizardStorage = useCallback(() => {
        try {
            if (typeof window !== 'undefined' && window.localStorage) {
                window.localStorage.removeItem(thisStorageKey)
                setState(createInitialStateRef.current())
            }
        } catch (error) {
            console.warn('Failed to clear wizard storage:', error)
        }
    }, [thisStorageKey])

    const recreateStepOrder = useCallback((newCurrentStep) => {
        setState((prev) => {
            const newStepOrder = []
            const viewModel = prev.viewModel

            // First pass: Add all visible top-level steps in their original order
            Object.keys(viewModel).forEach((stepName) => {
                const step = viewModel[stepName]
                // Add visible top-level steps
                if (step.visible !== false) {
                    newStepOrder.push(stepName)
                }
            })

            // Second pass: Insert visible children after their parents
            const finalStepOrder = []
            newStepOrder.forEach((stepName) => {
                const step = viewModel[stepName]
                finalStepOrder.push(stepName)

                // If this step has children, add visible ones right after the parent
                if (step.children && Object.keys(step.children).length > 0) {
                    Object.keys(step.children).forEach((childName) => {
                        const child = step.children[childName]
                        if (child.visible !== false) {
                            finalStepOrder.push(childName)
                        }
                    })
                }
            })

            // Find the correct index for currentStepName
            const targetStepName = newCurrentStep || prev.currentStepName
            const currentStepIndex = finalStepOrder.findIndex((step) => step === targetStepName)
            const validIndex = currentStepIndex !== -1 ? currentStepIndex : 0

            return {
                ...prev,
                stepOrder: finalStepOrder,
                currentStep: validIndex,
                currentStepName: targetStepName,
            }
        })
    }, [])

    const dataForStepper = {
        format: state.contentCache?.selectFormat?.format,
        showLeadForm: state.contentCache?.selectFormat?.showLeadForm ?? false,
        assetSource: state.contentCache?.uploadAssets?.source,
        assetType: state.contentCache?.uploadAssets?.selectedOption,
        newLeadForm: state.contentCache?.leadForm?.showNewLeadForm,
    }

    return (
        <WizardContext.Provider
            value={{
                id: state.id,
                extractedId,
                currentStep: state.currentStep,
                currentStepName,
                stepOrder: state.stepOrder,
                goNext,
                goBack,
                goTo,
                goToStep,
                setVisible,
                setChildrenVisibility,
                registerNextGuard,
                deregisterNextGuard,
                purgeNextGuards,
                hasActiveGuards,
                registerSteps,
                viewModel: state.viewModel,
                setData,
                setStepContentData,
                getStepContentData,
                removeStepContentData,
                clearStepAndChildren,
                storageKey: thisStorageKey,
                wizardState: state,
                // Custom navigation
                customNavigation,
                registerCustomNavigation,
                clearCustomNavigation,
                clearWizardStorage,
                recreateStepOrder,
                markStepAs: markStepAs,
                // Ad Creative specific (backward compatibility)
                // Use campaignIdProp when provided (edit mode); else extractedId (new creative uses campaignId as storage key)
                campaignId: campaignIdProp ?? extractedId,
                campaignFormat: state.campaignFormat || 'singleImage',
                setCampaignFormat: (format) => setState((prev) => ({ ...prev, campaignFormat: format })),
                dataForStepper: dataForStepper,
                // Edit mode
                editCreativeId: state.editCreativeId || null,
                // Clone mode - source creative ID for copying assets
                cloneCreativeId: cloneCreativeId || null,
                // ID for fetching assets: edit/clone use source creative; new uses current wizard id
                // When cloning with new assets (upload or media library), use temp creative id
                assetAdId: (() => {
                    if (state.editCreativeId) return state.editCreativeId
                    const hasCloneWithNewAssets = cloneCreativeId && state.contentCache?.uploadAssets?.cloneWithNewAssets
                    if (cloneCreativeId && !hasCloneWithNewAssets) return cloneCreativeId
                    return state.id
                })(),
                // Guide popups control
                showGuidePopups: state.showGuidePopups ?? true,
                setShowGuidePopups: (value) => setState((prev) => ({ ...prev, showGuidePopups: value })),
            }}
        >
            {children}
        </WizardContext.Provider>
    )
}

const useWizard = () => {
    const ctx = useContext(WizardContext)
    if (!ctx) throw new Error('useWizard must be used within WizardProvider')
    return ctx
}

export { WizardProvider, useWizard, WizardContext }
